import { useTranslation } from 'react-i18next';
import { detect, manualSteps, type InstallIcon } from '../../services/installApp';
import './InstallInstructions.css';

// Iconos SVG inline (sin imágenes externas: funcionan offline y no chocan con la
// CSP). Cada uno reproduce el botón real del navegador/SO.
const ICON_PATHS: Record<InstallIcon, string> = {
  // Compartir (cuadrado con flecha hacia arriba) — iOS.
  share: 'M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z',
  // Cuadrado con "+" — Añadir a pantalla de inicio.
  add: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z',
  // Tres puntos (⋮) — menú.
  menu: 'M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
  // Rejilla — Aplicaciones (Edge).
  apps: 'M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z',
  // Flecha de descarga — Instalar.
  install: 'M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z',
  // Brújula — Safari.
  safari: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.24 4.24l-2.47 6.53-6.53 2.47 2.47-6.53 6.53-2.47zM12 10.9c-.61 0-1.1.49-1.1 1.1s.49 1.1 1.1 1.1 1.1-.49 1.1-1.1-.49-1.1-1.1-1.1z',
};

function StepIcon({ icon }: { icon: InstallIcon }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
      <path d={ICON_PATHS[icon]} />
    </svg>
  );
}

// Modal de instrucciones de instalación manual, según plataforma/navegador.
// Se abre cuando la instalación automática no está disponible (iOS, Firefox…).
export function InstallInstructions({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  if (!open) return null;
  const guide = manualSteps(detect(), t);

  return (
    <div className="install-inst-backdrop" onClick={onClose}>
      <div className="install-inst" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="install-inst__head">
          <h3 className="install-inst__title">{guide.title}</h3>
          <button
            type="button"
            className="install-inst__close"
            onClick={onClose}
            aria-label={t('components.install.guide.close')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>

        {guide.steps.length > 0 && (
          <ol className="install-inst__steps">
            {guide.steps.map((s, i) => (
              <li key={i} className="install-inst__step">
                <span className="install-inst__num">{i + 1}</span>
                <span className="install-inst__text">{s.text}</span>
                {s.icon && <span className="install-inst__ic"><StepIcon icon={s.icon} /></span>}
              </li>
            ))}
          </ol>
        )}

        {guide.note && <p className="install-inst__note">{guide.note}</p>}

        <button type="button" className="install-inst__done" onClick={onClose}>
          {t('components.install.guide.gotIt')}
        </button>
      </div>
    </div>
  );
}
